import axios from 'axios'
import config from '../../../config'
import {
    TenantEscalationConfig,
    EscalationLevel,
    EscalationEntity,
    EscalationEntityMap,
    UserEscalationAssignment,
    UserEscalationConfig,
} from '../types/types'
import { ESCALATION_ENTITIES } from '../constants/escalation'

const { apiServerUrl } = config

export const emptyEntityMap = (): EscalationEntityMap =>
    ESCALATION_ENTITIES.reduce((acc, entity) => {
        acc[entity] = []
        return acc
    }, {} as EscalationEntityMap)

const escalationPath = (tenantEsKey: string | number) =>
    `tenant-onboard/${encodeURIComponent(String(tenantEsKey))}/escalation-config`

const onboardingUsersPath = (tenantEsKey: string | number) =>
    `onboarding-users/tenant/${encodeURIComponent(String(tenantEsKey))}/onboarding-users`

const isEscalationEntity = (value: unknown): value is EscalationEntity =>
    ESCALATION_ENTITIES.includes(value as EscalationEntity)

/** Target entities are an array; tolerate a bare string from older payloads. */
export const toEntityList = (value: unknown): EscalationEntity[] => {
    const list = Array.isArray(value) ? value : value ? [value] : []
    return list.filter(isEscalationEntity)
}

const toLevelConfig = (value: unknown): EscalationLevel | null => {
    const row = value as Partial<EscalationLevel> | null | undefined
    if (!row?.level) return null
    const triggerValue = Number(row.triggerValue)
    return {
        level: String(row.level),
        triggerValue: Number.isFinite(triggerValue) ? triggerValue : 0,
        triggerUnit: row.triggerUnit as EscalationLevel['triggerUnit'],
        channel: row.channel as EscalationLevel['channel'],
    }
}

/**
 * Read a tenant escalation config as one ladder per entity. Configs written
 * before this were a flat `levels[]` carrying an `entity` array — fan each of
 * those into every entity it listed so nothing disappears from the UI.
 */
export const toEntityMap = (config: unknown): EscalationEntityMap => {
    const cfg = config as
        | { entities?: unknown; levels?: unknown }
        | null
        | undefined
    const map = emptyEntityMap()

    const entities = cfg?.entities as
        | Record<string, { levels?: unknown } | unknown[] | undefined>
        | undefined
    if (entities && typeof entities === 'object') {
        ESCALATION_ENTITIES.forEach((entity) => {
            const bucket = entities[entity]
            const nested = (bucket as { levels?: unknown } | undefined)?.levels
            const levels: unknown[] = Array.isArray(bucket)
                ? bucket
                : Array.isArray(nested)
                  ? nested
                  : []
            map[entity] = levels
                .map(toLevelConfig)
                .filter((l): l is EscalationLevel => l !== null)
        })
        return map
    }

    if (Array.isArray(cfg?.levels)) {
        (cfg!.levels as unknown[]).forEach((raw) => {
            const level = toLevelConfig(raw)
            if (!level) return
            const targets = toEntityList((raw as { entity?: unknown })?.entity)
            const applyTo = targets.length ? targets : ESCALATION_ENTITIES
            applyTo.forEach((entity) => map[entity].push(level))
        })
    }

    return map
}

export const hasConfiguredLevels = (map: EscalationEntityMap): boolean =>
    ESCALATION_ENTITIES.some((entity) => (map[entity] ?? []).length > 0)

/**
 * Coerce a stored user escalation config to the current (level, entity[]) pairs.
 * Rows written before entities existed hold `{ levels: ['II'] }` or
 * `{ level: 'II' }` — read those as applying to every entity so editing an older
 * user never silently drops the assignment.
 */
export const toAssignmentList = (config: unknown): UserEscalationAssignment[] => {
    const cfg = config as { levels?: unknown; level?: unknown } | null | undefined

    const raw = Array.isArray(cfg?.levels)
        ? (cfg!.levels as unknown[])
        : cfg?.level
          ? [cfg.level]
          : []

    const merged = new Map<string, Set<EscalationEntity>>()
    raw.forEach((item) => {
        const isPair = item !== null && typeof item === 'object'
        const level = String(
            isPair ? ((item as UserEscalationAssignment).level ?? '') : item
        ).trim()
        if (!level) return
        const entities = isPair
            ? toEntityList((item as UserEscalationAssignment).entity)
            : []
        const applyTo = entities.length ? entities : ESCALATION_ENTITIES
        const bucket = merged.get(level) ?? new Set<EscalationEntity>()
        applyTo.forEach((entity) => bucket.add(entity))
        merged.set(level, bucket)
    })

    const assignments: UserEscalationAssignment[] = []
    merged.forEach((entities, level) => {
        assignments.push({
            level,
            entity: ESCALATION_ENTITIES.filter((entity) => entities.has(entity)),
        })
    })
    return assignments
}

/** Levels a user holds for one entity. */
export const levelsForEntity = (
    config: unknown,
    entity: EscalationEntity
): string[] =>
    toAssignmentList(config)
        .filter((assignment) => assignment.entity.includes(entity))
        .map((assignment) => assignment.level)

/**
 * Rebuild a user escalation config from per-entity level picks. Levels selected
 * for several entities collapse back into one assignment row.
 */
export const buildUserEscalation = (
    picksByEntity: Partial<Record<EscalationEntity, string[]>>
): UserEscalationConfig | null => {
    const merged = new Map<string, Set<EscalationEntity>>()
    ESCALATION_ENTITIES.forEach((entity) => {
        ;(picksByEntity[entity] ?? []).forEach((level) => {
            const bucket = merged.get(level) ?? new Set<EscalationEntity>()
            bucket.add(entity)
            merged.set(level, bucket)
        })
    })
    const levels: UserEscalationAssignment[] = []
    merged.forEach((entities, level) => {
        levels.push({
            level,
            entity: ESCALATION_ENTITIES.filter((entity) => entities.has(entity)),
        })
    })
    return levels.length ? { levels } : null
}

export const normalizeUserEscalation = (
    config: unknown
): UserEscalationConfig | null => {
    const levels = toAssignmentList(config)
    return levels.length ? { levels } : null
}

/**
 * Fetch configured escalation levels for a tenant.
 * GET /admin/tenant-onboard/:tenantEsKey/escalation-config
 *
 * On the /admin surface, not /onboarding-users: this app holds a casa-realm
 * token, and /onboarding-users/* is validated against the tenant-onboard-test
 * realm instead (which would reject it as an invalid token).
 *
 * Throws on failure. A swallowed error here would read as "this tenant has no
 * escalation levels", and the next save would wipe the levels it never loaded.
 */
export const getTenantEscalationConfig = async (
    tenantEsKey: string | number
): Promise<TenantEscalationConfig> => {
    try {
        const { data } = await axios.get<{ success: boolean; data: unknown }>(
            `${apiServerUrl}/admin/${escalationPath(tenantEsKey)}`
        )
        const payload = (data as any)?.data ?? data
        return { entities: toEntityMap(payload) }
    } catch (err: any) {
        console.error('Failed to fetch tenant escalation config', err)
        throw new Error(
            err?.response?.data?.message ||
                err?.message ||
                'Failed to fetch tenant escalation config'
        )
    }
}

/**
 * Create or replace configured escalation levels for a tenant.
 * PUT /admin/tenant-onboard/:tenantEsKey/escalation-config
 */
export const updateTenantEscalationConfig = async (
    tenantEsKey: string | number,
    entities: EscalationEntityMap
): Promise<TenantEscalationConfig> => {
    try {
        const body = {
            entities: ESCALATION_ENTITIES.reduce(
                (acc, entity) => {
                    acc[entity] = { levels: entities[entity] ?? [] }
                    return acc
                },
                {} as Record<EscalationEntity, { levels: EscalationLevel[] }>
            ),
        }
        const { data } = await axios.put<{ success: boolean; data: unknown }>(
            `${apiServerUrl}/admin/${escalationPath(tenantEsKey)}`,
            body
        )
        const payload = (data as any)?.data
        return payload ? { entities: toEntityMap(payload) } : { entities }
    } catch (err: any) {
        console.error('Failed to update tenant escalation config', err)
        throw err
    }
}

/**
 * Create an onboarding user with escalation level.
 * POST /onboarding-users/tenant/:tenantEsKey/onboarding-users
 */
export const createOnboardingUserWithEscalation = async (
    tenantEsKey: string | number,
    userPayload: {
        email: string
        name: string
        type: 'internal' | 'external'
        permissions?: string[]
        moduleIds?: number[]
        escalationConfig?: UserEscalationConfig | null
    }
) => {
    const { data } = await axios.post(
        `${apiServerUrl}/${onboardingUsersPath(tenantEsKey)}`,
        userPayload
    )
    return data
}

/**
 * Update an onboarding user escalation level.
 * PATCH /onboarding-users/tenant/:tenantEsKey/onboarding-users/:userId
 */
export const updateOnboardingUserEscalation = async (
    tenantEsKey: string | number,
    userId: number | string,
    escalationConfig: UserEscalationConfig
) => {
    const { data } = await axios.patch(
        `${apiServerUrl}/${onboardingUsersPath(tenantEsKey)}/${encodeURIComponent(String(userId))}`,
        { escalationConfig }
    )
    return data
}
